import { useState } from 'react';
import { LogOut, Settings, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationBell } from '../NotificationBell';
import { AdminAlertsBell } from '../AdminAlertsBell';

import logo from '../../assets/images/logo.svg';
import { Button } from '../ui';
import { ThemeToggle } from '../../theme/ThemeToggle';
import { NgrokStatus } from './NgrokStatus';

import { Container, Content } from './styles';

interface HeaderProps {
  onMenuClick: () => void;
  menuOpen: boolean;
}

export function Header({ onMenuClick, menuOpen }: HeaderProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <Container>
      <Content>
        {/* ── Left: hamburger + logo + brand ── */}
        <div className="flex items-center gap-2.5">
          <Button
            variant="ghost"
            size="sm"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-controls="sidebar"
            aria-expanded={menuOpen}
            onClick={onMenuClick}
            className="h-9 w-9 flex-shrink-0 rounded-xl border border-border transition-colors hover:bg-primary/10 hover:text-primary"
          >
            <span className="sr-only">{menuOpen ? 'Cerrar' : 'Abrir'} menú</span>
            <span
              className="flex flex-col items-center justify-center gap-[5px] transition-all duration-300"
              aria-hidden
            >
              <span
                className={`h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ${menuOpen ? 'translate-y-[6.5px] rotate-45' : ''
                  }`}
              />
              <span
                className={`h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ${menuOpen ? 'opacity-0' : ''
                  }`}
              />
              <span
                className={`h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ${menuOpen ? '-translate-y-[6.5px] -rotate-45' : ''
                  }`}
              />
            </span>
          </Button>

          <img src={logo} alt="WaiterApp logo" className="h-8 w-8 flex-shrink-0" loading="lazy" decoding="async" />

          <span className="text-sm font-bold uppercase tracking-widest text-text">
            Waiter<span className="text-primary">App</span>
          </span>
        </div>

        {/* ── Center: empty on mobile, search on desktop ── */}
        <div className="hidden flex-1 md:flex" />

        {/* ── Right: theme + profile ── */}
        <div className="flex items-center justify-end gap-1.5">
          <NgrokStatus />
          <ThemeToggle />

          {/* Admin live alerts (exclusivo para perfil Administrador) */}
          <AdminAlertsBell />

          {/* Notification bell */}
          <NotificationBell />

          {/* Profile button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Menú de usuario"
              aria-expanded={profileOpen}
              onClick={() => setProfileOpen((prev) => !prev)}
              className="h-9 w-9 rounded-xl border border-border hover:bg-primary/10 hover:text-primary"
            >
              <User className="h-4 w-4" aria-hidden />
            </Button>

            {profileOpen && (
              <>
                {/* Backdrop for closing */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setProfileOpen(false)}
                  aria-hidden
                />
                <div
                  className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
                  role="menu"
                >
                  {/* User info header */}
                  <div className="border-b border-border px-4 py-3">
                    <p className="text-sm font-semibold text-text">Admin</p>
                    <p className="text-xs text-text-muted">Administrador</p>
                  </div>

                  <div className="p-1.5">
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-text transition-colors hover:bg-muted"
                      role="menuitem"
                      onClick={() => setProfileOpen(false)}
                    >
                      <User size={15} className="text-text-muted" />
                      Perfil
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-text transition-colors hover:bg-muted"
                      role="menuitem"
                      onClick={() => setProfileOpen(false)}
                    >
                      <Settings size={15} className="text-text-muted" />
                      Configuración
                    </Link>
                    <div className="my-1 border-t border-border" />
                    <Link
                      to="/logout"
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-danger transition-colors hover:bg-danger/10"
                      role="menuitem"
                      onClick={() => setProfileOpen(false)}
                    >
                      <LogOut size={15} />
                      Cerrar sesión
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Content>
    </Container>
  );
}
